import type {
  DeletedMappingInput,
  SaveMappingsResponse,
  StageCategoriesBoardResponse,
  StageCategoriesStatsResponse,
  StageCategoryMappingInput,
} from "@shared/types/stageCategories";

import apiClient from "../axios";

/** PUT body for saveMappings — mappings plus explicit deletions. */
export interface SaveMappingsBody {
  deletedMappings?: DeletedMappingInput[];
  mappings: StageCategoryMappingInput[];
}

// Augment axios config type so `skipMaintenanceRedirect` is recognized.
declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * Opt out of the global 503 → /error-503 redirect when a route uses
     * 503 as a typed business-logic response (see stage-categories PUT).
     */
    skipMaintenanceRedirect?: boolean;
  }
}

const stageCategoriesApi = {
  /** GET /admin/stage-categories — full Kanban board state. */
  fetchBoard: async (): Promise<StageCategoriesBoardResponse> => {
    const response = await apiClient.get<StageCategoriesBoardResponse>("/admin/stage-categories");
    return response.data;
  },

  /** GET /admin/stage-categories/stats — lightweight dashboard badge counts. */
  fetchStats: async (): Promise<StageCategoriesStatsResponse> => {
    const response = await apiClient.get<StageCategoriesStatsResponse>(
      "/admin/stage-categories/stats"
    );
    return response.data;
  },

  /**
   * PUT /admin/stage-categories — apply mappings + explicit deletions with
   * per-mapping optimistic locking (each carries clientUpdatedAt). Passes
   * skipMaintenanceRedirect so a 503 TRANSACTION_TIMEOUT or STATEMENT_TIMEOUT
   * reaches the caller instead of redirecting to the maintenance page. 409
   * (CONFLICT) is handled by the caller — see stageCategoriesStore.save().
   */
  saveMappings: async (body: SaveMappingsBody): Promise<SaveMappingsResponse> => {
    const response = await apiClient.put<SaveMappingsResponse>("/admin/stage-categories", body, {
      skipMaintenanceRedirect: true,
    });
    return response.data;
  },
};

export default stageCategoriesApi;
